import { useEffect, useState } from "react";
import { FaFacebookF, FaInstagram, FaTiktok, FaWhatsapp } from "react-icons/fa6";
import { FiMail, FiPhone, FiMapPin, FiX } from "react-icons/fi";
import { SiGooglemaps } from "react-icons/si";
import { HiOutlineGlobeAlt, HiSparkles } from "react-icons/hi2";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useApiClient from "hooks/useApiClient";
import { useHotelContext } from "hooks/hotel/useHotelContext";
import useGetHotelAvailability from "hooks/hotel/useGetHotelAvailability";

const apps = [
    {
        icon: FaFacebookF,
        link: "https://www.facebook.com/share/1FG6qmTjfr/?mibextid=wwXIfr",
        label: "Facebook"
    },
    {
        icon: FaInstagram,
        link: "https://www.instagram.com/aquaterra_travel?igshid=bzdvMnNsZXNkYXVl&utm_source=qr",
        label: "Instagram"
    },
    {
        icon: FaTiktok,
        link: "https://www.tiktok.com/@jevoyageici?_t=ZS-8zdlus39zI9&_r=1",
        label: "TikTok"
    },
    {
        icon: FaWhatsapp,
        link: "https://wa.me/213770268725",
        label: "WhatsApp"
    },    
];

const Footer = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { urls, api } = useApiClient();
    const { setHotelSearchParams, setProviders, hotelSearchParams } = useHotelContext();
    const { getHotelAvailability } = useGetHotelAvailability();
    const [activeModal, setActiveModal] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [startHotelSearch, setStartHotelSearch] = useState(false);
    const [pendingHotelState, setPendingHotelState] = useState(null);
    const [notification, setNotification] = useState(null);

    const closeModal = () => {
        setActiveModal(null);
        setSelectedCity(null);
    };
    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        setNotification(`${label} copié!`);
        setTimeout(() => setNotification(null), 2000);
    };
    function generateCheckInDate() {
        const today = new Date();
        today.setDate(today.getDate() + 7);
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    function generateCheckOutDate() {
        const today = new Date();
        today.setDate(today.getDate() + 12);
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    const handleHotelSearchByCity = async (cityLabel) => {
        try {
            const res = await api.get(urls.hotel.cityAutocomplete, {
                params: {
                    term: cityLabel,
                    language: i18n.language,
                },
            });

            const predictions = res?.data?.predictions || [];
            const destination = predictions?.[0]
                ? {
                      id: predictions[0].id,
                      description: predictions[0].description,
                      descriptionEn: predictions[0].descriptionEn,
                  }
                : null;

            const providers = (res?.data?.providers || []).map((p) => ({
                id: p.id,
                code: p.code,
            }));

            if (!destination || providers.length === 0) return;

            const nextHotelState = {
                ...hotelSearchParams,
                destination,
                checkInDate: generateCheckInDate(),
                checkOutDate: generateCheckOutDate(),
            };

            setHotelSearchParams((prev) => ({
                ...prev,
                destination: nextHotelState.destination,
                checkInDate: nextHotelState.checkInDate,
                checkOutDate: nextHotelState.checkOutDate,
            }));

            setProviders(() => [...providers]);
            setPendingHotelState(nextHotelState);
            setStartHotelSearch(true);
            closeModal();
        } catch (e) {
            return;
        }
    };

    const runHotelSearch = async () => {
        navigate("/hotel-search");
        await getHotelAvailability();
        localStorage.setItem("hotelState", JSON.stringify(pendingHotelState || hotelSearchParams));
        setStartHotelSearch(false);
        setPendingHotelState(null);
    };

    useEffect(() => {
        if (startHotelSearch) {
            runHotelSearch();
        }
    }, [startHotelSearch]);

    const topCities = [
        { id: "hammamet_tunisia", label: t("footerCityHammametTunisia"), searchTerm: "Hammamet" },
        { id: "istanbul_turkey", label: t("footerCityIstanbulTurkey"), searchTerm: "Istanbul" },
        { id: "cairo_egypt", label: t("footerCityCairoEgypt"), searchTerm: "Cairo" },
        { id: "kualaLumpur_malaysia", label: t("footerCityKualaLumpurMalaysia"), searchTerm: "Kuala Lumpur" },
        { id: "bangkok_thailand", label: t("footerCityBangkokThailand"), searchTerm: "Bangkok" },
        { id: "paris_france", label: t("footerCityParisFrance"), searchTerm: "Paris" },
        { id: "barcelona_spain", label: t("footerCityBarcelonaSpain"), searchTerm: "Barcelona" },
    ];

    const FooterData = [
        {
            heading: t("links"),
            items: [
                { label: t("aboutUs"), action: "about" },
                { label: t("contactUs"), action: "contact" },
                { label: t("topCities"), action: "cities" },
                { label: t("termsAndConditions"), link: "/assets/termesConditions.pdf", external: true },
            ],
        },
        {
            heading: t("topCities"),
            items: topCities.map((city) => ({
                label: city.label,
                action: "cities",
                city: city.id,
            })),
        },
    ];

    return (
        <>
            <footer className="relative bg-gradient-to-b from-gray-50 to-white border-t border-gray-200 mt-20 overflow-hidden">
                {/* Decorative Background Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-24 -right-24 w-96 h-96 bg-gray-100/50 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 -left-24 w-96 h-96 bg-gray-100/50 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto px-4 pt-16 pb-0 relative z-10">
                    {/* Main Footer Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                        {/* Contact Section */}
                        <div className="lg:col-span-5">
                            <div className="space-y-6">
                                <div>
                                    <h5 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                                        <HiSparkles className="text-blue-600" />
                                        {t("needHelp")}
                                    </h5>
                                    <p className="text-gray-600 text-sm">{t("call247")}</p>
                                </div>

                                {/* Phone Numbers */}
                                <div className="space-y-3">
                                    {["+213770268725"].map((phone, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => copyToClipboard(phone, 'Numéro')}
                                            className="flex items-center gap-3 p-3 rounded-xl bg-white border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all group w-full text-left"
                                        >
                                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                                                <FiPhone className="text-blue-700" size={18} />
                                            </div>
                                            <span className="font-semibold text-gray-900">{phone}</span>
                                        </button>
                                    ))}
                                </div>

                                {/* Email */}
                                <div>
                                    <p className="text-sm text-gray-600 mb-2">{t("mailUs")}</p>
                                    <button
                                        type="button"
                                        onClick={() => copyToClipboard('jevoyageici@gmail.com', 'Email')}
                                        className="flex items-center gap-3 p-3 rounded-xl bg-white border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all group w-full text-left"
                                    >
                                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                                            <FiMail className="text-blue-700" size={18} />
                                        </div>
                                        <span className="font-medium text-gray-900">jevoyageici@gmail.com</span>
                                    </button>
                                </div>
                                {/* Google Maps */}
                                <div>
                                    <a
                                        href="https://www.google.com/maps/search/Aquaterra+Travel+Alger"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-3 p-3 rounded-xl bg-white border border-red-200 hover:border-red-400 hover:shadow-md transition-all group w-full text-left"
                                    >
                                        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                                            <SiGooglemaps className="text-red-500" size={18} />
                                        </div>
                                        <div>
                                            <span className="font-semibold text-gray-900 block">{t("findUs") || "Notre agence"}</span>
                                            <span className="text-sm text-gray-500">Google Maps</span>
                                        </div>
                                    </a>
                                </div>

                                {/* Social Media */}
                                <div>
                                    <p className="text-sm text-gray-600 mb-3">{t("followUs")}</p>
                                    <div className="flex gap-3">
                                        {apps.map((app, idx) => {
                                            const Icon = app.icon;
                                            return (
                                                <a
                                                    key={idx}
                                                    href={app.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="w-12 h-12 rounded-xl bg-white border border-blue-200 hover:border-blue-400 hover:shadow-md flex items-center justify-center transition-all hover:scale-110"
                                                    aria-label={app.label}
                                                >
                                                    <Icon className="text-blue-700" size={20} />
                                                </a>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Links Sections */}
                        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-8">
                            {FooterData.map((section, index) => (
                                <div key={index}>
                                    <h5 className="text-lg font-bold text-gray-900 mb-4">{section.heading}</h5>
                                    <ul className="space-y-2">
                                        {section.items.map((item, idx) => (
                                            <li key={idx}>
                                                {item.external ? (
                                                    <a
                                                        href={item.link}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-gray-600 hover:text-gray-900 transition-colors text-sm"
                                                    >
                                                        {item.label}
                                                    </a>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedCity(item.city ?? null);
                                                            setActiveModal(item.action);
                                                        }}
                                                        className="text-gray-600 hover:text-gray-900 transition-colors text-sm text-left"
                                                    >
                                                        {item.label}
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Company Info Card */}
                    <div className="relative rounded-3xl bg-gradient-to-br from-blue-600 via-blue-800 to-green-600 p-8 md:p-12 text-white overflow-hidden">
                        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjIiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40"></div>

                        <div className="relative z-10">
                            <div className="flex flex-col lg:flex-row gap-8 items-start">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-4">
                                        <HiOutlineGlobeAlt size={24} />
                                        <h5 className="text-2xl font-bold">{t("aboutUs")}</h5>
                                    </div>
                                    <p className="text-gray-100 text-lg mb-6">
                                        {t("footerCompanyDescription")}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:max-w-xl w-full">
                                    {[
                                        { title: t("footerFeature1Title"), desc: t("footerFeature1Desc") },
                                        { title: t("footerFeature2Title"), desc: t("footerFeature2Desc") },
                                        { title: t("footerFeature3Title"), desc: t("footerFeature3Desc") },
                                        { title: t("footerFeature4Title"), desc: t("footerFeature4Desc") },
                                    ].map((item, idx) => (
                                        <div key={idx} className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 border border-white/30 hover:bg-white/20 transition-all">
                                            <div className="font-bold text-sm mb-2">{item.title}</div>
                                            <div className="text-gray-300 text-xs leading-relaxed">{item.desc}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-8 pt-6 border-t border-white/30">
                                <p className="text-lg font-semibold text-gray-100">
                                    {t("footerTaglineLine1")} <br className="hidden sm:block" />
                                    <span className="text-gray-200">{t("footerTaglineLine2")}</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Copyright */}
                    <div className="pt-4 pb-8 border-t border-gray-100 text-center">
                        <p className="text-gray-600 text-sm">
                            {new Date().getFullYear()} Aquaterra Travel. {t("footerAllRightsReserved")}
                        </p>
                    </div>
                </div>
            </footer>
            {/* Floating WhatsApp Button */}
            <a
                href="https://wa.me/213770268725"
                target="_blank"
                rel="noreferrer"
                aria-label="Discuter sur WhatsApp"
                className="wa-float-btn fixed bottom-24 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] text-white transition-transform duration-200"
                style={{ boxShadow: "0 4px 20px rgba(37,211,102,0.4)" }}
            >
                <FaWhatsapp size={30} />
            </a>

            {notification && (
                <div className="fixed top-4 right-4 z-50 animate-slideDown">
                    <div className="bg-gray-900 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3">
                        <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="font-medium">{notification}</span>
                    </div>
                </div>
            )}
            {/* Modal */}
            {activeModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
                    <div className={`bg-white rounded-3xl shadow-2xl w-full ${activeModal === "cities" ? "max-w-4xl" : "max-w-2xl"} max-h-[90vh] overflow-hidden animate-slideUp`}>
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-2xl font-bold text-gray-900">
                                {activeModal === "about" && t("aboutUs")}
                                {activeModal === "contact" && t("contactUs")}
                                {activeModal === "cities" && t("topCities")}
                            </h3>

                            <button
                                type="button"
                                onClick={closeModal}
                                className="w-10 h-10 rounded-full bg-white hover:bg-gray-100 flex items-center justify-center transition-colors"
                            >
                                <FiX size={20} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-88px)]">
                            {activeModal === "about" && (
                                <div className="space-y-6">
                                    <div className="rounded-2xl bg-gray-50 p-6">
                                        <div className="text-xl font-bold text-gray-900 mb-2">{t("footerMissionTitle")}</div>
                                        <div className="text-gray-700 leading-relaxed">
                                            {t("footerMissionDescription")}
                                        </div>
                                    </div>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        {[
                                            { title: t("footerAboutCard1Title"), desc: t("footerAboutCard1Desc") },
                                            { title: t("footerAboutCard2Title"), desc: t("footerAboutCard2Desc") },
                                            { title: t("footerAboutCard3Title"), desc: t("footerAboutCard3Desc") },
                                            { title: t("footerAboutCard4Title"), desc: t("footerAboutCard4Desc") },
                                        ].map((item, idx) => (
                                            <div key={idx} className="rounded-2xl border-2 border-gray-200 p-5 hover:border-gray-400 hover:shadow-lg transition-all">
                                                <div className="font-bold text-gray-900 mb-2">{item.title}</div>
                                                <div className="text-gray-600 text-sm">{item.desc}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeModal === "contact" && (
                                <div className="grid gap-6 md:grid-cols-2">
                                    <div className="rounded-2xl border-2 border-gray-200 p-6 hover:border-gray-400 hover:shadow-lg transition-all">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                                <FiPhone className="text-gray-700" size={20} />
                                            </div>
                                            <div className="font-bold text-gray-900 text-lg">{t("footerContactPhoneTitle")}</div>
                                        </div>

                                        <div className="space-y-2">
                                            {["+213770268725", "+213770722259"].map((phone, idx) => (
                                                <button 
                                                    key={idx} 
                                                    onClick={() => copyToClipboard(phone, 'Numéro')}
                                                    className="block text-gray-700 hover:text-gray-900 font-medium cursor-pointer"
                                                >
                                                    {phone}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="rounded-2xl border-2 border-gray-200 p-6 hover:border-gray-400 hover:shadow-lg transition-all">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                                <FiMail className="text-gray-700" size={20} />
                                            </div>
                                            <div className="font-bold text-gray-900 text-lg">{t("mailUs")}</div>
                                        </div>
                                        <button 
                                            onClick={() => copyToClipboard('jevoyageici@gmail.com', 'Email')}
                                            className="text-gray-700 hover:text-gray-900 font-medium cursor-pointer"
                                        >
                                            jevoyageici@gmail.com
                                        </button>
                                    </div>
                                    <div className="rounded-2xl border-2 border-gray-200 p-6 hover:border-gray-400 hover:shadow-lg transition-all">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                                <FiMapPin className="text-gray-700" size={20} />
                                            </div>
                                            <div className="font-bold text-gray-900 text-lg">{t("footerContactSocialTitle")}</div>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            {apps.map((app, idx) => {
                                                const Icon = app.icon;
                                                return (
                                                    <a
                                                        key={idx}
                                                        href={app.link}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="w-14 h-14 rounded-2xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all hover:scale-110"
                                                    >
                                                        <Icon className="text-gray-700" size={22} />
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="rounded-2xl border-2 border-red-100 p-6 hover:border-red-300 hover:shadow-lg transition-all">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                                                <SiGooglemaps className="text-red-500" size={20} />
                                            </div>
                                            <div className="font-bold text-gray-900 text-lg">{t("findUs") || "Notre agence"}</div>
                                        </div>
                                        <a
                                            href="https://www.google.com/maps/search/Aquaterra+Travel+Alger"
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl transition-colors font-medium text-sm"
                                        >
                                            <SiGooglemaps size={16} />
                                            {t("openInMaps") || "Ouvrir dans Maps"}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {activeModal === "cities" && (
                                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                    {topCities
                                        .slice()
                                        .sort((a, b) => (a.id === selectedCity ? -1 : b.id === selectedCity ? 1 : 0))
                                        .map((city) => (
                                            <button
                                                key={city.id}
                                                type="button"
                                                onClick={() => handleHotelSearchByCity(city.searchTerm)}
                                                className={`group rounded-2xl p-6 text-left transition-all hover:scale-105 ${
                                                    selectedCity === city.id
                                                        ? "bg-gradient-to-br from-blue-500 to-purple-500 text-white shadow-xl"
                                                        : "bg-white border-2 border-gray-200 hover:border-blue-400 hover:shadow-lg"
                                                }`}
                                            >
                                                <div className={`text-lg font-bold mb-2 ${selectedCity === city.id ? "text-white" : "text-gray-900"}`}>
                                                    {city.label}
                                                </div>
                                                <div className={`text-sm ${selectedCity === city.id ? "text-blue-100" : "text-gray-600"}`}>
                                                    {t("footerDiscoverOffers")}
                                                </div>
                                            </button>
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>
        </>
    );
};

export default Footer;